import type {
  AuditTrailRow,
  BillingVsRevenue,
  DataQuality,
  LifetimeValueReport,
  MonthlyTrendPoint,
  OccupancyMetrics,
  PacientesMetrics,
  PlanBilling,
  PlanRankingRow,
  ProfesionalesMetrics,
  TurnosMetrics,
} from '@/application/metrics';
import type { EmailReportTable } from '@/infrastructure/http/metricsApi';

export interface ReportEmailPayload {
  kpis?: Array<{ label: string; value: string | number }>;
  tables?: EmailReportTable[];
}

const RIGHT = 'right' as const;

function kpiTable(
  title: string,
  rows: Array<[string, string | number]>
): EmailReportTable {
  return {
    title,
    columns: [
      { key: 'metrica', header: 'Métrica', align: 'left' },
      { key: 'valor', header: 'Valor', align: RIGHT },
    ],
    rows: rows.map(([metrica, valor]) => ({ metrica, valor })),
  };
}

export function occupancyEmailPayload(data: OccupancyMetrics): ReportEmailPayload {
  return {
    kpis: [
      { label: 'Slots usados', value: data.usedSlots },
      { label: 'Capacidad', value: data.capacitySlots },
      { label: 'Ocupación %', value: Math.round(data.occupancyRate * 1000) / 10 },
      { label: 'Método', value: data.method },
    ],
    tables: [
      {
        title: 'Ocupación por sede',
        columns: [
          { key: 'sede', header: 'Sede', align: 'left' },
          { key: 'usados', header: 'Usados', align: RIGHT },
          { key: 'capacidad', header: 'Capacidad', align: RIGHT },
          { key: 'ocupacion', header: 'Ocupación %', align: RIGHT },
        ],
        rows: data.bySede.map((row) => ({
          sede: row.sede,
          usados: row.usedSlots,
          capacidad: row.capacitySlots,
          ocupacion: Math.round(row.occupancyRate * 1000) / 10,
        })),
      },
    ],
  };
}

export function lifetimeValueEmailPayload(data: LifetimeValueReport): ReportEmailPayload {
  return {
    kpis: [
      { label: 'Pacientes con ingresos', value: data.summary.patientsWithRevenue },
      { label: 'Total atribuido', value: data.summary.totalAttributed },
      { label: 'Sin atribuir', value: data.summary.totalUnattributed },
      { label: 'LTV promedio', value: data.summary.avgLtv },
    ],
    tables: [
      {
        title: 'Top pacientes por ingresos',
        columns: [
          { key: 'paciente', header: 'Paciente', align: 'left' },
          { key: 'legajo', header: 'Legajo', align: 'left' },
          { key: 'ingresos', header: 'Ingresos', align: RIGHT },
          { key: 'movimientos', header: 'Movimientos', align: RIGHT },
        ],
        rows: data.topPatients.slice(0, 50).map((row) => ({
          paciente: row.pacienteNombre || `#${row.pacienteId}`,
          legajo: row.legajo || '',
          ingresos: row.totalIngresos,
          movimientos: row.movimientos,
        })),
      },
    ],
  };
}

export function auditTrailEmailPayload(rows: AuditTrailRow[]): ReportEmailPayload {
  return {
    kpis: [{ label: 'Eventos', value: rows.length }],
    tables: [
      {
        title: 'Auditoría de accesos',
        columns: [
          { key: 'cuando', header: 'Cuándo', align: 'left' },
          { key: 'usuario', header: 'Usuario', align: 'left' },
          { key: 'rol', header: 'Rol', align: 'left' },
          { key: 'concepto', header: 'Concepto', align: 'left' },
          { key: 'detalle', header: 'Detalle', align: 'left' },
        ],
        rows: rows.slice(0, 200).map((row) => ({
          cuando: row.at,
          usuario: row.usuarioEmail || '',
          rol: row.rol || '',
          concepto: row.concepto,
          detalle: row.detalle || '',
        })),
      },
    ],
  };
}

export function turnosEmailPayload(data: TurnosMetrics, title = 'Turnos'): ReportEmailPayload {
  return {
    tables: [
      kpiTable(title, [
        ['Total', data.total],
        ['Atendidos', data.atendidos],
        ['Cancelados', data.cancelados],
        ['No asistió', data.noAsistio],
        ['Reservados', data.reservados],
        ['% Asistencia', data.rates.attendance],
        ['% Cancelación', data.rates.cancellation],
        ['% Ausentismo', data.rates.absenteeism],
      ]),
    ],
  };
}

export function cancellationsEmailPayload(data: TurnosMetrics): ReportEmailPayload {
  const lost = data.cancelados + data.noAsistio;
  const lossRate = data.total > 0 ? Math.round((lost / data.total) * 1000) / 10 : 0;
  return {
    kpis: [
      { label: 'Cancelados', value: data.cancelados },
      { label: 'No asistió', value: data.noAsistio },
      { label: 'Slots perdidos', value: lost },
      { label: '% Pérdida', value: lossRate },
    ],
    tables: turnosEmailPayload(data, 'Detalle de turnos').tables,
  };
}

export function pacientesEmailPayload(data: PacientesMetrics): ReportEmailPayload {
  return {
    tables: [
      kpiTable('Pacientes', [
        ['Nuevos', data.nuevos],
        ['Activos', data.activos],
        ['Atendidos', data.atendidos],
      ]),
    ],
  };
}

export function profesionalesEmailPayload(data: ProfesionalesMetrics): ReportEmailPayload {
  const hasDeltas = data.profesionales.some((row) => row.trend !== undefined);
  return {
    kpis: [
      { label: 'Profesionales', value: data.totales.profesionales },
      { label: 'Turnos atendidos', value: data.totales.turnosAtendidos },
      { label: 'Ingresos', value: data.totales.ingresos },
      ...(hasDeltas
        ? [
            { label: 'En crecimiento', value: data.totales.enCrecimiento ?? 0 },
            { label: 'En caída', value: data.totales.enCaida ?? 0 },
          ]
        : []),
    ],
    tables: [
      {
        title: 'Producción por profesional',
        columns: [
          { key: 'nombre', header: 'Profesional', align: 'left' },
          { key: 'turnos', header: 'Turnos', align: RIGHT },
          { key: 'ingresos', header: 'Ingresos', align: RIGHT },
          ...(hasDeltas
            ? [
                { key: 'delta', header: 'Δ ingresos %', align: RIGHT },
                { key: 'trend', header: 'Tendencia', align: 'left' as const },
              ]
            : []),
        ],
        rows: data.profesionales.slice(0, 100).map((row) => ({
          nombre: row.nombre,
          turnos: row.turnosAtendidos,
          ingresos: row.ingresos,
          ...(hasDeltas
            ? {
                delta: row.deltaIngresosPct ?? '—',
                trend: row.trend ?? 'flat',
              }
            : {}),
        })),
      },
    ],
  };
}

export function consolidatedEmailPayload(
  clinics: Array<{
    label: string;
    turnosTotal: number;
    turnosAtendidos: number;
    pacientesActivos: number;
    cajaIngresos: number;
    cajaNeto: number;
  }>,
  totals: {
    clinics: number;
    turnosTotal: number;
    turnosAtendidos: number;
    pacientesActivos: number;
    cajaIngresos: number;
    cajaNeto: number;
  }
): ReportEmailPayload {
  return {
    kpis: [
      { label: 'Clínicas', value: totals.clinics },
      { label: 'Turnos atendidos', value: totals.turnosAtendidos },
      { label: 'Pacientes activos', value: totals.pacientesActivos },
      { label: 'Ingresos', value: totals.cajaIngresos },
      { label: 'Neto', value: totals.cajaNeto },
    ],
    tables: [
      {
        title: 'Resumen por clínica',
        columns: [
          { key: 'clinica', header: 'Clínica', align: 'left' },
          { key: 'turnos', header: 'Turnos', align: RIGHT },
          { key: 'atendidos', header: 'Atendidos', align: RIGHT },
          { key: 'pacientes', header: 'Pac. activos', align: RIGHT },
          { key: 'ingresos', header: 'Ingresos', align: RIGHT },
          { key: 'neto', header: 'Neto', align: RIGHT },
        ],
        rows: clinics.map((c) => ({
          clinica: c.label,
          turnos: c.turnosTotal,
          atendidos: c.turnosAtendidos,
          pacientes: c.pacientesActivos,
          ingresos: c.cajaIngresos,
          neto: c.cajaNeto,
        })),
      },
    ],
  };
}

export function annualEmailPayload(
  year: number,
  yearPoints: MonthlyTrendPoint[],
  totals: {
    turnosAtendidos: number;
    ingresos: number;
    egresos: number;
    neto: number;
    pacientesNuevos: number;
    planesVendidos: number;
  }
): ReportEmailPayload {
  return {
    kpis: [
      { label: `Atendidos ${year}`, value: totals.turnosAtendidos },
      { label: `Ingresos ${year}`, value: totals.ingresos },
      { label: `Neto ${year}`, value: totals.neto },
      { label: `Pac. nuevos ${year}`, value: totals.pacientesNuevos },
    ],
    tables: [
      {
        title: `Detalle mensual ${year}`,
        columns: [
          { key: 'periodo', header: 'Período', align: 'left' },
          { key: 'atendidos', header: 'Atendidos', align: RIGHT },
          { key: 'ingresos', header: 'Ingresos', align: RIGHT },
          { key: 'egresos', header: 'Egresos', align: RIGHT },
          { key: 'neto', header: 'Neto', align: RIGHT },
          { key: 'nuevos', header: 'Nuevos', align: RIGHT },
          { key: 'planes', header: 'Planes', align: RIGHT },
        ],
        rows: yearPoints.map((p) => ({
          periodo: p.periodo,
          atendidos: p.turnosAtendidos,
          ingresos: p.ingresos,
          egresos: p.egresos,
          neto: p.neto,
          nuevos: p.pacientesNuevos,
          planes: p.planesVendidos,
        })),
      },
    ],
  };
}

export function planRankingEmailPayload(rows: PlanRankingRow[]): ReportEmailPayload {
  return {
    tables: [
      {
        title: 'Ranking de planes',
        columns: [
          { key: 'plan', header: 'Plan', align: 'left' },
          { key: 'cantidad', header: 'Cantidad', align: RIGHT },
          { key: 'facturacion', header: 'Facturación', align: RIGHT },
          { key: 'previa', header: 'Cant. previa', align: RIGHT },
        ],
        rows: rows.slice(0, 100).map((row) => ({
          plan: row.planNombre,
          cantidad: row.cantidad,
          facturacion: Math.round((Number(row.facturacion) || 0) * 100) / 100,
          previa: row.cantidadPrevia,
        })),
      },
    ],
  };
}

export function trendEmailPayload(points: MonthlyTrendPoint[]): ReportEmailPayload {
  return {
    tables: [
      {
        title: 'Tendencia mensual',
        columns: [
          { key: 'periodo', header: 'Período', align: 'left' },
          { key: 'atendidos', header: 'Atendidos', align: RIGHT },
          { key: 'ingresos', header: 'Ingresos', align: RIGHT },
          { key: 'neto', header: 'Neto', align: RIGHT },
          { key: 'nuevos', header: 'Nuevos', align: RIGHT },
          { key: 'planes', header: 'Planes', align: RIGHT },
        ],
        rows: points.map((p) => ({
          periodo: p.periodo,
          atendidos: p.turnosAtendidos,
          ingresos: p.ingresos,
          neto: p.neto,
          nuevos: p.pacientesNuevos,
          planes: p.planesVendidos,
        })),
      },
    ],
  };
}

export function dataQualityEmailPayload(data: DataQuality): ReportEmailPayload {
  return {
    kpis: [
      { label: 'Tiene warnings', value: data.hasWarnings ? 'Sí' : 'No' },
      { label: 'Checks OK', value: data.checks.filter((c) => c.status === 'ok').length },
      { label: 'Warnings', value: data.checks.filter((c) => c.status === 'warning').length },
    ],
    tables: [
      {
        title: 'Controles de calidad',
        columns: [
          { key: 'check', header: 'Control', align: 'left' },
          { key: 'status', header: 'Estado', align: 'left' },
          { key: 'detail', header: 'Valores', align: 'left' },
        ],
        rows: data.checks.map((c) => ({
          check: c.key,
          status: c.status,
          detail: Object.entries(c.values)
            .map(([k, v]) => `${k}=${v}`)
            .join(', '),
        })),
      },
    ],
  };
}

export function planBillingEmailPayload(
  billing: PlanBilling,
  vs?: BillingVsRevenue | null
): ReportEmailPayload {
  const kpis: Array<{ label: string; value: string | number }> = [
    { label: 'Plan', value: billing.planNombre || '' },
    { label: 'Estado', value: billing.estado || '' },
    { label: 'Al día', value: billing.alDia ? 'Sí' : 'No' },
    { label: 'Monto', value: billing.monto },
    { label: 'Deuda acumulada', value: billing.deudaAcumulada },
    { label: 'Medio de pago', value: billing.medioPago || '' },
  ];
  if (vs) {
    kpis.push(
      { label: 'Ingresos clínica', value: vs.ingresos },
      { label: 'Neto clínica', value: vs.neto },
      {
        label: '% plan sobre ingresos',
        value:
          vs.cargaPlanSobreIngresos == null
            ? ''
            : Math.round(vs.cargaPlanSobreIngresos * 1000) / 10,
      },
      { label: 'Nivel', value: vs.nivel }
    );
  }
  return { kpis };
}
